import { useState } from "react";
import {
	Drawer,
	DrawerClose,
	// DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import type { ProgressiveVideo, VimeoResponse } from "@/api/vimeo.types";
import { calculateVideoSizeMB } from "@/utils/videoDownloadService";
import { Download } from "lucide-react";
import { DrawerContentWithoutCancelButton } from "./ui/dropdownMobile";

interface ResolutionOption {
	resolution: string;
	video: ProgressiveVideo;
	sizeMB: number;
}

interface DownloadVideoDrawerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	videoName: string;
	vimeoData?: VimeoResponse | null;
	onConfirmDownload: (
		resolution: string,
		downloadUrl: string,
		sizeMB: number
	) => void;
	videoSource: "vimeo_source" | "akademia_source" | "unknown";
	akademiaVideoUrl?: string;
	estimatedSizeMB?: number;
}

export const DownloadVideoDrawer = ({
	open,
	onOpenChange,
	videoName,
	vimeoData,
	onConfirmDownload,
	videoSource,
	akademiaVideoUrl,
	estimatedSizeMB = 0,
}: DownloadVideoDrawerProps): JSX.Element => {
	const [selectedResolution, setSelectedResolution] = useState<string>("");
	const [selectedUrl, setSelectedUrl] = useState<string>("");
	const [selectedSize, setSelectedSize] = useState<number>(0);

	// Get available resolutions from Vimeo
	const resolutionOptions: Array<ResolutionOption> = vimeoData?.play
		?.progressive
		? vimeoData.play.progressive
				.map((video) => ({
					resolution: `${video.height}p`,
					video,
					sizeMB: calculateVideoSizeMB(video),
				}))
				.sort((a, b) => b.video.height - a.video.height)
		: [];

	const handleResolutionSelect = (option: ResolutionOption): void => {
		setSelectedResolution(option.resolution);
		setSelectedUrl(option.video.link);
		setSelectedSize(option.sizeMB);
	};

	const handleConfirm = (): void => {
		if (videoSource === "akademia_source" && akademiaVideoUrl) {
			// For akademia source, download directly
			onConfirmDownload("original", akademiaVideoUrl, estimatedSizeMB);
		} else if (selectedResolution && selectedUrl && selectedSize) {
			// For Vimeo, use selected resolution
			onConfirmDownload(selectedResolution, selectedUrl, selectedSize);
		}
		onOpenChange(false);
	};

	const canDownload =
		(videoSource === "akademia_source" && akademiaVideoUrl) ||
		(videoSource === "vimeo_source" && selectedResolution);

	return (
		<Drawer open={open} onOpenChange={onOpenChange}>
			<DrawerContentWithoutCancelButton>
				<DrawerHeader className="text-left p-8">
					<div className="flex justify-between items-start">
						<div className="flex-1">
							<DrawerTitle className="text-lg font-semibold">
								Czy potwierdzasz pobranie na urządzenie pliku:
							</DrawerTitle>
							<DrawerDescription className="mt-2 text-sm">
								{videoName}
							</DrawerDescription>
						</div>
					</div>
				</DrawerHeader>

				<div className="px-8 pb-6 space-y-4">
					{videoSource === "vimeo_source" && resolutionOptions.length > 0 && (
						<div>
							<p className="text-sm font-medium mb-3">Wybierz rozdzielczość:</p>
							<div className="space-y-2">
								{resolutionOptions.map((option) => (
									<button
										key={option.resolution}
										onClick={() => {
											handleResolutionSelect(option);
										}}
										className={`w-full p-3 rounded-lg border-2 transition-all ${
											selectedResolution === option.resolution
												? "border-blue-500 bg-blue-50"
												: "border-gray-200 hover:border-gray-300"
										}`}
									>
										<div className="flex justify-between items-center">
											<span className="font-medium">{option.resolution}</span>
											<span className="text-sm text-gray-600">
												{option.sizeMB} MB
											</span>
										</div>
									</button>
								))}
							</div>
						</div>
					)}

					{videoSource === "akademia_source" && (
						<div className="p-4 bg-gray-50 rounded-lg">
							<p className="text-sm">
								<span className="font-medium">Rozmiar: </span>~{estimatedSizeMB}{" "}
								MB
							</p>
						</div>
					)}

					{videoSource === "unknown" && (
						<div className="p-4 bg-red-50 rounded-lg">
							<p className="text-sm text-red-600">
								Nie można pobrać tego wideo. Nieznane źródło.
							</p>
						</div>
					)}

					<div className="bg-yellow-50 p-3 rounded-lg">
						<p className="text-xs text-yellow-800">
							<strong>Uwaga:</strong> Wideo zostanie pobrane na pamięć
							urządzenia. Upewnij się, że masz wystarczająco dużo miejsca.
						</p>
					</div>

					<div className="flex justify-between gap-2 pt-2">
						<DrawerClose asChild>
							<Button variant="link" className="underline text-gray-400">
								Nie pobieraj
							</Button>
						</DrawerClose>
						<Button
							onClick={handleConfirm}
							disabled={!canDownload}
							className=" border-[1px] border-[#919EAB3D] px-2 py-1"
							variant="ghost"
						>
							<Download className="mr-2 h-4 w-4" />
							Tak, Pobierz
						</Button>
					</div>
				</div>
			</DrawerContentWithoutCancelButton>
		</Drawer>
	);
};
